// This is the base component that is used to create the other FloatingLabelInput components.
import { SelectInput as VFSelectInput } from "@vfuk/core-select-input/dist/SelectInput";
import { SelectInputProps as VFSelectInputProps } from "@vfuk/core-select-input/dist/SelectInput.types";
import { TextInput as VFTextInput } from "@vfuk/core-text-input/dist/TextInput";
import { TextInputProps as VFTextInputProps } from "@vfuk/core-text-input/dist/TextInput.types";
import classNames from "classnames";
import { ReactElement, useEffect, useState } from "react";
import { SelectComponentsProps } from "react-select/src/Select";
import classes from "./FloatingLabelBase.module.scss";
import Spacing from "@vfuk/core-spacing";
import Icon from "@vfuk/core-icon";

type FloatingLabelInputProps =
  | ({
      label: string;
      children: ReactElement<VFTextInput> | ReactElement<VFSelectInput>;
      forceFloat?: boolean;
      spaceForError?: boolean;
      fieldHeightSmall?: boolean;
      error?: string;
      className?: string;
      showPassword?: boolean;
      hideEyeSymbol?: boolean;
      onFocus?: () => void;
      onShowPassword?: () => void;
      readonly?: true;
    } & VFSelectInputProps &
      VFTextInputProps)
  | SelectComponentsProps;

const FloatingLabelBase = ({
  label,
  error,
  forceFloat,
  spaceForError = true,
  fieldHeightSmall = false,
  children,
  showPassword,
  className,
  hideEyeSymbol,
  type,
  onFocus,
  onShowPassword,
  readonly,
  ...inputProps
}: FloatingLabelInputProps): JSX.Element => {
  const [mounted, setMounted] = useState(false);
  const [focused, setFocused] = useState(false);
  const [labelFloated, setLabelFloated] = useState(
    inputProps.value ? true : false
  );

  const onFocused = () => {
    setLabelFloated(true);
    setFocused(true);
    onFocus && onFocus();
  };

  const onBlurred = () => {
    setFocused(false);
    if (!inputProps.value) {
      setLabelFloated(false);
    }
  };

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (mounted && !inputProps.value && !focused) {
      setLabelFloated(false);
    }
  }, [inputProps.value]);

  return (
    <div
      className={classNames(classes.FloatingLabelInput, className, {
        [classes.SpaceForError]: spaceForError,
        [classes.FieldHeightSmall]: fieldHeightSmall,
        [classes.Focused]: focused,
        [classes.Active]: forceFloat || labelFloated,
        [classes.Disabled]:
          inputProps.state === "disabled" ||
          ("disabled" in inputProps && inputProps.disabled),
        [classes.Readonly]: readonly,
        [classes.Error]: inputProps.state === "error" || error,
        [classes.Warn]: inputProps.state === "warn",
        [classes.Success]: inputProps.state === "success",
      })}
      onFocus={onFocused}
      onBlur={onBlurred}
    >
      <label htmlFor={inputProps.id}>{label}</label>
      {children}
      {error && (
        <span className={classes.ErrorText} role="alert">
          <Spacing spacingLevel={{ right: 2 }}>
            <Icon name="error" group="state" size={2} />
          </Spacing>
          {error}
        </span>
      )}
      {type === "password" && !hideEyeSymbol && (
        <span className={classes.RightSide}>
          <Spacing spacingLevel={{ left: 1 }}>
            <span style={{ cursor: "pointer" }} onClick={onShowPassword}>
              <Icon
                name={showPassword ? "hide-password" : "show-password"}
                size={2}
                appearance="primary"
              />
            </span>
          </Spacing>
        </span>
      )}
    </div>
  );
};

export default FloatingLabelBase;
